import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  ActiveMarkschemeEditorItem,
  ActiveMarkschemeEditorItemCoordinates,
  ActiveMarkschemeEditorSubItemCoordinates,
  createMarkschemeFormGroup,
  MARKSCHEME_ITEM_TYPE_TO_COLOR,
  MARKSCHEME_ITEM_TYPE_TO_LONG_LABEL,
  MARKSCHEME_ITEM_TYPE_TO_SHORT_LABEL,
  MarkschemeEditorItemEnum,
  MarkschemeFormGroup,
} from '@examdojo/markscheme-v2';
import { ContentItem } from '@examdojo/models/content-item';
import { StemMarkScheme } from '@examdojo/models/markscheme-v2';
import {
  Option,
  OptionsItem,
  OptionsMarkscheme,
  QuestionItemTypeEnum,
  QuestionItemV2,
  StemCoordinates,
  StemGroupItemV2,
  StemItemV2,
  StimulusItemV2,
} from '@examdojo/models/question';
import { QuestionEditingOptions } from '@examdojo/models/question-editor';
import { ContentItemFormGroup, createContentItemsFormArray } from '@examdojo/question-content-item';

export type EditorItemType = QuestionItemTypeEnum | MarkschemeEditorItemEnum;

export type TopLevelFormGroup = StemFormGroup | StimulusFormGroup | StemGroupFormGroup | OptionsItemFormGroup;

// TODO - custom colors
export const CONTENT_ITEM_TYPE_TO_COLOR: Record<EditorItemType, string> = {
  [QuestionItemTypeEnum.Stem]: 'orange',
  [QuestionItemTypeEnum.Stimulus]: 'purple',
  [QuestionItemTypeEnum.StemGroup]: 'indigo',
  [QuestionItemTypeEnum.Options]: 'green',
  ...MARKSCHEME_ITEM_TYPE_TO_COLOR,
};

export const CONTENT_ITEM_TYPE_TO_SHORT_LABEL: Record<EditorItemType, string> = {
  [QuestionItemTypeEnum.Stem]: 'Stem',
  [QuestionItemTypeEnum.Stimulus]: 'Stim',
  [QuestionItemTypeEnum.StemGroup]: 'StemG',
  [QuestionItemTypeEnum.Options]: 'MCQ',
  ...MARKSCHEME_ITEM_TYPE_TO_SHORT_LABEL,
};

export const CONTENT_ITEM_TYPE_TO_LONG_LABEL: Record<EditorItemType, string> = {
  [QuestionItemTypeEnum.Stem]: 'Stem',
  [QuestionItemTypeEnum.Stimulus]: 'Stimulus',
  [QuestionItemTypeEnum.StemGroup]: 'Stem Group',
  [QuestionItemTypeEnum.Options]: 'Multiple Choice Options',
  ...MARKSCHEME_ITEM_TYPE_TO_LONG_LABEL,
};

export type ActiveQuestionEditorItem =
  | {
      ctrl: TopLevelFormGroup;
      type: QuestionItemTypeEnum;
    }
  | ActiveMarkschemeEditorItem;

export type ActiveQuestionEditorItemCoordinates =
  | StemCoordinates
  | (StemCoordinates & ActiveMarkschemeEditorItemCoordinates);

export type ActiveQuestionEditorSubItemCoordinates =
  | {
      content_items: number;
    }
  | ActiveMarkschemeEditorSubItemCoordinates
  | ActiveQuestionEditorSubItemOptionsCoordinates;

export type ActiveQuestionEditorCoordinates = Partial<
  ActiveQuestionEditorItemCoordinates & ActiveQuestionEditorSubItemCoordinates
>;

export type StemFormGroup = FormGroup<{
  content_items: FormArray<ContentItemFormGroup>;
  type: FormControl<StemItemV2['type']>;
  markscheme: MarkschemeFormGroup | FormControl<null>;
}>;

export function createStemFormGroup(
  stem: StemItemV2 | undefined = {
    type: QuestionItemTypeEnum.Stem,
    content_items: [],
    markscheme: null,
  },
  options: QuestionEditingOptions,
): StemFormGroup {
  return new FormGroup({
    content_items: createContentItemsFormArray(stem.content_items, {
      allowTextEditing: options.allowQuestionEditing,
      allowImageEditing: options.allowImageEditing,
      allowFillInTheBlanks: true,
      hideImageAndTableMetadata: options.hideImageAndTableMetadata,
    }),
    type: new FormControl(stem.type, { nonNullable: true }),
    markscheme: stem.markscheme ? createMarkschemeFormGroup(stem.markscheme, options) : new FormControl(null),
  });
}

export type StemGroupFormGroup = FormGroup<{
  type: FormControl<StemGroupItemV2['type']>;
  stems: FormArray<StemFormGroup>;
  content_items: FormArray<ContentItemFormGroup>;
}>;

export function createStemGroupFormGroup(
  stemGroup: StemGroupItemV2 | undefined = {
    type: QuestionItemTypeEnum.StemGroup,
    content_items: [],
    stems: [],
  },
  options: QuestionEditingOptions,
): StemGroupFormGroup {
  return new FormGroup({
    stems: new FormArray<StemFormGroup>(stemGroup.stems.map((stem) => createStemFormGroup(stem, options))),
    content_items: createContentItemsFormArray(stemGroup.content_items, {
      allowTextEditing: options.allowQuestionEditing,
      allowImageEditing: options.allowImageEditing,
      allowFillInTheBlanks: false,
      hideImageAndTableMetadata: options.hideImageAndTableMetadata,
    }),
    type: new FormControl(stemGroup.type, { nonNullable: true }),
  });
}

export type StimulusFormGroup = FormGroup<{
  content_items: FormArray<ContentItemFormGroup>;
  type: FormControl<StimulusItemV2['type']>;
}>;

export function createStimulusFormGroup(
  stimulus: StimulusItemV2 | undefined = {
    type: QuestionItemTypeEnum.Stimulus,
    content_items: [],
  },
  options: QuestionEditingOptions,
): StimulusFormGroup {
  return new FormGroup({
    content_items: createContentItemsFormArray(stimulus.content_items, {
      allowTextEditing: options.allowQuestionEditing,
      allowImageEditing: options.allowImageEditing,
      allowFillInTheBlanks: false,
      hideImageAndTableMetadata: options.hideImageAndTableMetadata,
    }),
    type: new FormControl(stimulus.type, { nonNullable: true }),
  });
}

export type OptionFormGroup = FormGroup<{
  content_items: FormArray<ContentItemFormGroup>;
}>;

export function createOptionFormGroup(
  option: Option | undefined = {
    content_items: [],
  },
  editingOptions: QuestionEditingOptions,
): OptionFormGroup {
  return new FormGroup({
    content_items: createContentItemsFormArray(option.content_items, {
      allowTextEditing: editingOptions.allowQuestionEditing,
      allowImageEditing: editingOptions.allowImageEditing,
      allowFillInTheBlanks: false,
      hideImageAndTableMetadata: editingOptions.hideImageAndTableMetadata,
    }),
  });
}

export type OptionsMarkschemeFormGroup = FormGroup<{
  marks: FormControl<OptionsMarkscheme['marks']>;
  correct_option_indexes: FormControl<OptionsMarkscheme['correct_option_indexes']>;
  max_selection_count?: FormControl<Required<OptionsMarkscheme>['max_selection_count']>;
  min_selection_count?: FormControl<Required<OptionsMarkscheme>['min_selection_count']>;
  topic_ids: FormControl<Required<OptionsMarkscheme>['topic_ids']>;
  worked_solution?: FormArray<ContentItemFormGroup>;
}>;

function createOptionsMarkschemeFormGroup(
  markscheme: OptionsMarkscheme,
  editingOptions: QuestionEditingOptions,
): OptionsMarkschemeFormGroup {
  return new FormGroup({
    marks: new FormControl(markscheme.marks, { nonNullable: true }),
    correct_option_indexes: new FormControl(markscheme.correct_option_indexes, { nonNullable: true }),
    ...(markscheme.max_selection_count !== undefined
      ? { max_selection_count: new FormControl(markscheme.max_selection_count, { nonNullable: true }) }
      : {}),
    ...(markscheme.min_selection_count !== undefined
      ? { min_selection_count: new FormControl(markscheme.min_selection_count, { nonNullable: true }) }
      : {}),
    topic_ids: new FormControl(markscheme.topic_ids, { nonNullable: true }),
    ...(markscheme.worked_solution
      ? {
          worked_solution: createContentItemsFormArray(markscheme.worked_solution, {
            allowTextEditing: editingOptions.allowMarkschemeEditing,
            allowImageEditing: editingOptions.allowImageEditing,
            allowFillInTheBlanks: false,
            hideImageAndTableMetadata: editingOptions.hideImageAndTableMetadata,
          }),
        }
      : {}),
  });
}

export type OptionsItemFormGroup = FormGroup<{
  content_items: FormArray<ContentItemFormGroup>;
  type: FormControl<OptionsItem['type']>;
  options: FormArray<OptionFormGroup>;
  markscheme: OptionsMarkschemeFormGroup;
}>;

export type ActiveQuestionEditorSubItemOptionsCoordinates =
  | {
      content_items: number;
    }
  | {
      content_items: number;
      options: number;
    }
  | {
      content_items: number;
      worked_solution: true;
    };

export function createOptionsItemFormGroup(
  options: OptionsItem | undefined = {
    type: QuestionItemTypeEnum.Options,
    content_items: [],
    options: [],
    markscheme: {
      marks: 0,
      correct_option_indexes: [],
      max_selection_count: 1,
      min_selection_count: 1,
      topic_ids: [],
      worked_solution: [],
    },
  },
  editingOptions: QuestionEditingOptions,
): OptionsItemFormGroup {
  return new FormGroup({
    content_items: createContentItemsFormArray(options.content_items, {
      allowTextEditing: editingOptions.allowQuestionEditing,
      allowImageEditing: editingOptions.allowImageEditing,
      allowFillInTheBlanks: false,
      hideImageAndTableMetadata: editingOptions.hideImageAndTableMetadata,
    }),
    type: new FormControl(options.type, { nonNullable: true }),
    options: new FormArray<OptionFormGroup>(
      options.options.map((option) => createOptionFormGroup(option, editingOptions)),
    ),
    markscheme: createOptionsMarkschemeFormGroup(options.markscheme, editingOptions),
  });
}

export type OptionsMarkschemeFormGroupValue = ReturnType<OptionsMarkschemeFormGroup['getRawValue']>;

export function getOptionsMarkschemeFormGroupValue(markscheme: OptionsMarkschemeFormGroupValue): OptionsMarkscheme {
  return {
    marks: markscheme.marks,
    correct_option_indexes: markscheme.correct_option_indexes,
    ...(markscheme.max_selection_count !== undefined ? { max_selection_count: markscheme.max_selection_count } : {}),
    ...(markscheme.min_selection_count !== undefined ? { min_selection_count: markscheme.min_selection_count } : {}),
    topic_ids: markscheme.topic_ids,
    // worked_solution will be handled separately in the calling code
  };
}

export type GetQuestionItem<T extends QuestionItemTypeEnum> = {
  [QuestionItemTypeEnum.Stem]: StemItemV2;
  [QuestionItemTypeEnum.Stimulus]: StimulusItemV2;
  [QuestionItemTypeEnum.StemGroup]: StemGroupItemV2;
  [QuestionItemTypeEnum.Options]: OptionsItem;
}[T];

export function populateQuestionItemsFormArray(
  formArray: FormArray<TopLevelFormGroup>,
  items: QuestionItemV2[],
  options: QuestionEditingOptions,
) {
  formArray.clear();

  for (const item of items) {
    switch (item.type) {
      case QuestionItemTypeEnum.Stem:
        formArray.push(createStemFormGroup(item, options));
        break;
      case QuestionItemTypeEnum.Stimulus:
        formArray.push(createStimulusFormGroup(item, options));
        break;
      case QuestionItemTypeEnum.StemGroup:
        formArray.push(createStemGroupFormGroup(item, options));
        break;
      case QuestionItemTypeEnum.Options:
        formArray.push(createOptionsItemFormGroup(item, options));
        break;
    }
  }
}

/**
 * Returns `true` when the diff between the question items is structural.
 * I.e. the types of some elements are different, or the number of items is different.
 */
export function isStructuralChange(oldActiveItems: QuestionItemV2[], newActiveItems: QuestionItemV2[]): boolean {
  if (oldActiveItems.length !== newActiveItems.length) {
    return true;
  }

  for (let i = 0; i < oldActiveItems.length; i++) {
    const oldItem = oldActiveItems[i];
    const newItem = newActiveItems[i];

    if (!isContentItemsStructureEqual(oldItem.content_items, newItem.content_items)) {
      return true;
    }

    if (oldItem.type !== newItem.type) {
      return true;
    }

    if (oldItem.type === QuestionItemTypeEnum.Stem && newItem.type === QuestionItemTypeEnum.Stem) {
      if (!isMarkschemeStructureEqual(oldItem.markscheme, newItem.markscheme)) {
        return true;
      }
    }

    if (oldItem.type === QuestionItemTypeEnum.StemGroup && newItem.type === QuestionItemTypeEnum.StemGroup) {
      for (let j = 0; j < oldItem.stems.length; j++) {
        if (!isContentItemsStructureEqual(oldItem.stems[j].content_items, newItem.stems[j].content_items)) {
          return true;
        }

        if (!isMarkschemeStructureEqual(oldItem.stems[j].markscheme, newItem.stems[j].markscheme)) {
          return true;
        }
      }
    }

    if (oldItem.type === QuestionItemTypeEnum.Options && newItem.type === QuestionItemTypeEnum.Options) {
      if (!isContentItemsStructureEqual(oldItem.content_items, newItem.content_items)) {
        return true;
      }

      for (let j = 0; j < oldItem.options.length; j++) {
        if (!isContentItemsStructureEqual(oldItem.options[j].content_items, newItem.options[j].content_items)) {
          return true;
        }
      }
    }
  }

  return false;

  function isContentItemsStructureEqual(oldContentItems: ContentItem[], newContentItems: ContentItem[]): boolean {
    if (oldContentItems.length !== newContentItems.length) {
      return false;
    }

    for (let i = 0; i < oldContentItems.length; i++) {
      if (oldContentItems[i].type !== newContentItems[i].type) {
        return false;
      }
    }

    return true;
  }

  function isMarkschemeStructureEqual(
    oldMarkscheme: StemMarkScheme | null,
    newMarkscheme: StemMarkScheme | null,
  ): boolean {
    if (!oldMarkscheme && !newMarkscheme) {
      return true;
    }

    if (!oldMarkscheme || !newMarkscheme) {
      return false;
    }

    if (oldMarkscheme.all_mark_groups.length !== newMarkscheme.all_mark_groups.length) {
      return false;
    }

    for (let i = 0; i < oldMarkscheme.all_mark_groups.length; i++) {
      if (
        !isContentItemsStructureEqual(
          oldMarkscheme.all_mark_groups[i].criterion,
          newMarkscheme.all_mark_groups[i].criterion,
        )
      ) {
        return false;
      }
    }

    if (oldMarkscheme.solution_paths.length !== newMarkscheme.solution_paths.length) {
      return false;
    }

    for (let i = 0; i < oldMarkscheme.solution_paths.length; i++) {
      if (
        !isContentItemsStructureEqual(
          oldMarkscheme.solution_paths[i].worked_solution,
          newMarkscheme.solution_paths[i].worked_solution,
        )
      ) {
        return false;
      }
    }

    return true;
  }
}
